import { HttpClient, HttpContext } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Persona } from '@app/v2/personas/types/persona.type'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { GenericTableSort, GenericTableSortDirection } from '@app/v2/shared/types/generic-table.types'
import { environment } from '@environments/environment'
import { Observable } from 'rxjs'
import { ArchivedItem, ArchivedItemsListResponse } from '../types/archived-item.type'
import { ArchivedItemsQueryFilters } from '../types/archived-items-filter.type'

@Injectable()
export class ArchivedItemsService {
  private readonly _http = inject(HttpClient)

  /**
   * Archive an item
   * @param workspaceId {number} - The id of the workspace
   * @param eventId {number} - The id of the event
   * @param itemId {number} - The id of the item to archive
   * @returns {Observable<Response<ArchivedItem>>} - An observable of the archived item
   */
  public archiveItems(workspaceId: number, eventId: number, itemId: number): Observable<Response<ArchivedItem>> {
    return this._http.post<Response<ArchivedItem>>(
      `${environment.planningApi}organizations/${workspaceId}/events/${eventId}/archived-items/${itemId}`,
      {},
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /**
   * Get archived items for an event
   * @param workspaceId {number} - The id of the workspace
   * @param eventId {number} - The id of the event
   * @param page {number} - The page number
   * @param perPage {number} - The number of items per page
   * @param sort {Pick<GenericTableSort, 'field' | 'direction'> & { field: string }} - The sort order
   * @param search {string} - The search term
   * @param filters {ArchivedItemsQueryFilters} - Optional type and date range filters
   * @returns {Observable<ArchivedItemsListResponse>} - An observable of the archived items
   */
  public getArchivedItems(
    workspaceId: number,
    eventId: number,
    page: number = 1,
    perPage: number = 10,
    sort?: Pick<GenericTableSort, 'field' | 'direction'> & { field: string },
    search?: string,
    filters?: ArchivedItemsQueryFilters
  ): Observable<ArchivedItemsListResponse> {
    let url = `${environment.planningApi}organizations/${workspaceId}/events/${eventId}/archived-items?page=${page}&per_page=${perPage}`

    if (sort?.field) {
      url += `&sort=${sort.direction || GenericTableSortDirection.DESC}`
      url += `&column=${encodeURIComponent(sort.field)}`
    }

    if (search) url += `&search=${encodeURIComponent(search)}`
    if (filters?.type) url += `&type=${encodeURIComponent(filters.type)}`
    if (filters?.archivedFrom) url += `&archived_from=${encodeURIComponent(filters.archivedFrom)}`
    if (filters?.archivedTo) url += `&archived_to=${encodeURIComponent(filters.archivedTo)}`

    return this._http.get<ArchivedItemsListResponse>(url, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Delete archived items
   * @param workspaceId {number} - The id of the workspace
   * @param eventId {number} - The id of the event
   * @param archivedItems {Pick<ArchivedItem, 'id' | 'type'>[]} - The archived items to delete
   * @returns {Observable<Response<ArchivedItem>>} - An observable of the deleted archived items
   */
  public deleteArchivedItems(workspaceId: number, eventId: number, archivedItems: Pick<ArchivedItem, 'id' | 'type'>[]): Observable<Response<ArchivedItem>> {
    return this._http.delete<Response<ArchivedItem>>(`${environment.planningApi}organizations/${workspaceId}/events/${eventId}/archived-items`, {
      body: { archived_items: archivedItems },
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Restore archived items
   * @param workspaceId {number} - The id of the workspace
   * @param eventId {number} - The id of the event
   * @param archivedItems {Pick<ArchivedItem, 'id' | 'type'>[]} - The archived items to restore
   * @returns {Observable<Response<ArchivedItem>>} - An observable of the restored archived items
   */
  public restoreArchivedItems(workspaceId: number, eventId: number, archivedItems: Pick<ArchivedItem, 'id' | 'type'>[]): Observable<Response<ArchivedItem>> {
    return this._http.patch<Response<ArchivedItem>>(
      `${environment.planningApi}organizations/${workspaceId}/events/${eventId}/archived-items/restore`,
      { archived_items: archivedItems },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /**
   * Collects linked persona ids for removal
   * @param target {Persona} - The target persona
   * @param personas {Persona[]} - The known personas
   * @returns {number[]} - The linked persona ids
   */
  public collectLinkedPersonaIdsForRemoval(target: Persona, personas: Persona[]): number[] {
    const ids = new Set<number>([target.id])
    const parentId = target.parent ?? null

    if (parentId != null) ids.add(parentId)

    for (const persona of personas) {
      const isLinkedSibling = parentId != null && persona.parent === parentId
      const isLinkedChild = parentId == null && persona.parent === target.id
      if (isLinkedSibling || isLinkedChild) {
        ids.add(persona.id)
      }
    }

    return Array.from(ids)
  }
}
